<template>
  <!--
    Regular alert toast. Each notification in the store renders its own card,
    stacked vertically (transient — most auto-dismiss after a few seconds via
    ApplicationLayout's timeOutOldNotifs). Visually identical to the priority
    order-fill toast for a unified aesthetic; severity is communicated through
    a subtle title tint, not chunky icons.
  -->
  <alert-card
    v-if="showNotification"
    :title="title"
    :detail="firstLineOfDetail"
    :variant="variant"
    :clickable="isClickable"
    @click="handleClick"
    @dismiss="removeNotification(notification.id)"
  />
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import AlertCard from '@/components/global/AlertCard.vue';
import { PostHogUtils, normalizeNotificationType } from '@/core/PostHogUtils';

const props = defineProps({
  index: {
    type: Number,
    required: true,
  },
  notification: {
    type: Object,
    required: true,
  },
  alertShown: {
    type: Boolean,
  },
});

const emit = defineEmits(['removeNotification']);

const route = useRoute();

const showNotification = computed(() => {
  return (
    typeof props.notification !== 'undefined' && props.notification !== null
  );
});

const title = computed(() => props.notification?.title || '');

const firstLineOfDetail = computed(() => {
  if (!props.notification?.detail) {
    return '';
  }
  // Defensive coercion: callers occasionally pass non-string values
  // (Error objects, response payloads, etc.). Sentry was catching
  // `detail.split is not a function` from those callers — see issue
  // 4CASTERS-FRONTEND-14G.
  const detail =
    typeof props.notification.detail === 'string'
      ? props.notification.detail
      : String(props.notification.detail);
  // The Monkey Machine view streams multi-line debug output and wants to
  // see all of it; everywhere else we treat detail as a single status line.
  if (route.name === 'MonkeyMachine') {
    return detail;
  }
  return detail.split('\n')[0] || '';
});

// Map the legacy status taxonomy onto AlertCard's variant. The accent
// colors are intentionally restrained (slim left bar + tinted mini-label,
// detail text stays neutral) so even the success cue reads as confident
// confirmation rather than a celebration.
const variant = computed(() => {
  const status = props.notification?.status;
  if (status === 'warning') return 'warning';
  if (status === 'cancel') return 'cancel';
  if (status === 'danger') return 'danger';
  if (status === 'ok' || status === 'ok & update') return 'success';
  return 'neutral';
});

// Only types that have a real click target are clickable. password-unsafe
// redirects to the account page where the user can change their password;
// other types currently have no destination, so we leave them non-clickable
// to keep the hover-lift cue honest.
const isClickable = computed(() => {
  return props.notification?.type === 'password-unsafe';
});

function handleClick() {
  // Before the redirect below — a full page load would abandon a queued event.
  // `type` is the notification's own stable kind ('bet-taken',
  // 'password-unsafe', ...); the message text never goes on the event.
  // Normalized so camelCase and kebab-case type names land as one convention.
  PostHogUtils.capture('notification_clicked', {
    notification_type: normalizeNotificationType(props.notification?.type),
    action: 'opened',
  });
  if (props.notification?.type === 'password-unsafe') {
    sessionStorage.setItem('scrollToPasswordSection', 'true');
    window.location.href = '/account/personal-info';
  }
}

function removeNotification(id) {
  emit('removeNotification', id);
}
</script>
